import React, { useCallback } from 'react';

import {
  useRefresh,
  useNotify,
  useRecordContext,
  useUpdate,
  Form,
  RecordContextProvider,
} from 'react-admin';

import {
  Box,
  DialogTitle,
  Button,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';

import useDialogStatus from '../../../hooks/useDialogStatus';

import { HandlePingForm, UpdatePingDialog } from '../../case/bulkActions/BulkUpdateDialog';
import { Ping } from '../../../utilities/schemas/ping';
import DraggableDialog from '../../layout/dialog/DraggableDialog';

const ManageStatusButton = ({
  handleClick,
  isIcon,
}: {
  handleClick: React.MouseEventHandler<HTMLButtonElement & { }>;
  isIcon?: boolean;
}) => {
  if (isIcon) {
    return (
      <IconButton
        color="primary"
        onClick={handleClick}
      >
        <EditIcon />
      </IconButton>
    );
  }
  return (
    <Button
      variant="contained"
      onClick={handleClick}
      value="ignored"
    >
      Manage status
    </Button>
  );
};

const handleId = 'update-ping-status-dialog-title';

const UpdatePingStatus = ({
  isIcon,
}: {
  isIcon?: boolean;
}) => {
  const refresh = useRefresh();
  const notify = useNotify();
  const record = useRecordContext<Ping>();
  const { open, openDialog, closeDialog } = useDialogStatus();
  const [update] = useUpdate();

  const onSubmit = useCallback((data: Partial<HandlePingForm>) => {
    update(
      'pings',
      {
        id: `${record?.id}/status`,
        data,
      },
      {
        onSuccess: () => {
          closeDialog();
          refresh();
        },
        onError: () => {
          notify('Could not update ping status', { type: 'error' });
        },
      },
    );
  }, [closeDialog, notify, record?.id, refresh, update]);
  return (
    <Box display="flex" maxWidth="md">
      <ManageStatusButton handleClick={openDialog} isIcon={isIcon} />
      <DraggableDialog
        open={open}
        onClose={closeDialog}
        fullWidth
        handleId={handleId}
        maxWidth="md"
      >
        <DialogTitle variant="h4" id={handleId}>
          Manage the status of the ping
        </DialogTitle>
        <Form
          mode="all"
          onSubmit={onSubmit}
          record={{
            status: record?.status,
            overrideRecommendation: record?.overrideRecommendation,
            numberOfDaysIgnoreRule: 0,
          }}
          sanitizeEmptyValues
        >
          <RecordContextProvider value={record}>
            <UpdatePingDialog
              closeDialog={closeDialog}
              variant="single"
            />
          </RecordContextProvider>
        </Form>
      </DraggableDialog>
    </Box>
  );
};

export default UpdatePingStatus;
